import {
  useEffect,
  useState,
  useMemo,
  KeyboardEvent,
  ClipboardEvent,
} from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';

import { AlertCircle, X } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/TailwindComponents';

interface SendEmailFormData {
  recipientEmail: string;
  emailBody: string;
  recipient: string;
  reason: string;
}

interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (emailData: {
    recipientEmail: string;
    emailBody: string;
    recipient: string;
    reason: string;
  }) => void;
  caseData: {
    caseId: string;
    caseType: string;
    status: string;
    createdBy: string;
  } | null;
  loading?: boolean;
}

interface EmailChip {
  email: string;
  isValid: boolean;
}

const validateEmail = (email: string): boolean => {
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailPattern.test(email.trim());
};

export const SendEmailModal = ({
  open,
  onClose,
  onConfirm,
  caseData,
  loading = false,
}: SendEmailModalProps) => {
  const getDefaultEmailBody = () => {
    if (caseData?.caseId) {
      return `An Internal Affairs report (ID: ${caseData.caseId}) has been shared with you.`;
    }
    return 'An Internal Affairs report has been shared with you.';
  };

  const defaultValues: SendEmailFormData = {
    recipientEmail: '',
    emailBody: getDefaultEmailBody(),
    recipient: '',
    reason: '',
  };

  const methods = useForm<SendEmailFormData>({
    defaultValues,
    mode: 'onSubmit',
  });

  const { handleSubmit, reset } = methods;
  const [inputValue, setInputValue] = useState('');

  const emailValue = methods.watch('recipientEmail') || '';

  const emailChips: EmailChip[] = useMemo(() => {
    if (!emailValue || typeof emailValue !== 'string') return [];
    return emailValue
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .map((email) => ({
        email,
        isValid: validateEmail(email),
      }));
  }, [emailValue]);

  const updateEmails = (chips: EmailChip[]) => {
    const emailString = chips.map((chip) => chip.email).join(', ');
    methods.setValue('recipientEmail', emailString, { shouldValidate: true });
    methods.clearErrors('recipientEmail');
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      !emailChips.some((chip) => chip.email === trimmedEmail)
    ) {
      const newChip: EmailChip = {
        email: trimmedEmail,
        isValid: validateEmail(trimmedEmail),
      };
      updateEmails([...emailChips, newChip]);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (
      e.key === 'Backspace' &&
      inputValue === '' &&
      emailChips.length > 0
    ) {
      const newChips = emailChips.slice(0, -1);
      updateEmails(newChips);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,;\s]+/).filter((email) => email.trim());

    const newChips = emails
      .filter(
        (email) => !emailChips.some((chip) => chip.email === email.trim()),
      )
      .map((email) => ({
        email: email.trim(),
        isValid: validateEmail(email.trim()),
      }));

    updateEmails([...emailChips, ...newChips]);
    setInputValue('');
  };

  const removeEmail = (index: number) => {
    const newChips = emailChips.filter((_, i) => i !== index);
    updateEmails(newChips);
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setInputValue('');
    }
  }, [open, reset]);

  const handleClose = () => {
    if (loading) return;
    reset();
    setInputValue('');
    onClose();
  };

  const handleFormSubmit = handleSubmit((data: SendEmailFormData) => {
    // Validate that all email chips are valid
    const hasInvalidEmails = emailChips.some((chip) => !chip.isValid);
    if (hasInvalidEmails) {
      methods.setError('recipientEmail', {
        type: 'manual',
        message: 'All email addresses must be valid',
      });
      return;
    }

    if (emailChips.length === 0) {
      methods.setError('recipientEmail', {
        type: 'manual',
        message: 'Recipient email address is required',
      });
      return;
    }

    onConfirm({
      recipientEmail: data.recipientEmail,
      emailBody: data.emailBody,
      recipient: data.recipient,
      reason: data.reason,
    });
    console.log(caseData);
  });

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleClose();
  };

  const emailError = methods.formState.errors.recipientEmail;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[592px] [&>button.absolute]:text-red-500">
        <DialogHeader className="border-b pb-3">
          <DialogTitle>Send email</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <FormProvider {...methods}>
            <form onSubmit={handleFormSubmit} className="space-y-3">
              {/* Recipient email address field with chips */}
              <div className="">
                <Label htmlFor="recipientEmail">
                  Recipient email address{' '}
                  <span className="text-red-500">*</span>
                </Label>

                <div
                  className={`border p-2 rounded-md min-h-[40px] bg-white dark:bg-gray-800 transition-all flex items-center flex-wrap gap-2 ${
                    emailError
                      ? 'border-red-500'
                      : 'border-[#CFD6DA] dark:border-gray-600'
                  }`}
                >
                  {emailChips.map((chip, index) => (
                    <div
                      key={index}
                      className={`inline-flex text-[#3E5A6A] h-[24px] items-center gap-1 px-1 rounded border text-sm ${
                        chip.isValid
                          ? 'bg-[#FFEFB3] border-[#FECB00]'
                          : 'bg-[#F9C9D1] border-[#EE3E5C]'
                      }`}
                    >
                      {!chip.isValid && (
                        <AlertCircle className="w-3.5 h-3.5 text-[#EE3E5C] ml-1" />
                      )}
                      <span>{chip.email}</span>
                      <button
                        type="button"
                        onClick={() => removeEmail(index)}
                        className="rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${chip.email}`}
                        disabled={loading}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}

                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    disabled={loading}
                    placeholder={emailChips.length === 0 ? '/' : ''}
                    className="flex-1 outline-none text-sm bg-transparent min-w-[50px]"
                  />
                </div>

                {emailError && (
                  <p className="text-sm text-red-500 mt-1">
                    {emailError.message}
                  </p>
                )}
              </div>

              {/* Recipient field */}
              <div className="space-y-1">
                <Label htmlFor="recipient">
                  Recipient <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="recipient"
                  control={methods.control}
                  rules={{ required: 'Recipient is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="recipient"
                      type="text"
                      placeholder="/"
                      disabled={loading}
                      className={
                        methods.formState.errors.recipient
                          ? 'border-red-500'
                          : ''
                      }
                    />
                  )}
                />
                {methods.formState.errors.recipient && (
                  <p className="text-sm text-red-500">
                    {methods.formState.errors.recipient.message}
                  </p>
                )}
              </div>

              {/* Reason field */}
              <div className="space-y-1">
                <Label htmlFor="reason">
                  Reason <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="reason"
                  control={methods.control}
                  rules={{ required: 'Reason is required' }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="reason"
                      placeholder="/"
                      rows={4}
                      disabled={loading}
                      className={
                        methods.formState.errors.reason ? 'border-red-500' : ''
                      }
                    />
                  )}
                />
                {methods.formState.errors.reason && (
                  <p className="text-sm text-red-500">
                    {methods.formState.errors.reason.message}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  className="rounded-md bg-white"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="rounded-md"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send email'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailModal;
